import React, { useEffect, useState } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Box, Paper, Typography, TextField, Button, Radio, RadioGroup, FormControlLabel,
  Checkbox, Alert, InputAdornment, Switch, IconButton, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, MenuItem, Select, FormControl, InputLabel, Chip
} from "@mui/material";
import { ArrowBack, Add, Delete } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useMarketingSettings, MARKETING_DEFAULTS, DAY_NAMES } from "../Campaign/hooks/useCampaign";
import { Loading } from "../Campaign/components/CampaignComponents";
import "../Campaign/Campaign.css";

const ROLES = [
  ["Supper", "Super Admin"],
  ["Admin", "Admin"],
  ["Team", "Team"],
  ["CallCenter", "Call Center"]
];

const AREAS = [
  ["marketingSettings", "Change these settings and automations"],
  ["campaigns", "Campaigns (create, send, results)"],
  ["aiCalling", "AI Calling section"],
  ["sipLine", "AI Calling → SIP line"],
  ["recordings", "AI Calling → listen to / download recordings"]
];

const FEATURES = [
  ["smsTracking", "SMS tracking", "Each member gets their own tracked link for {TrackingLink}"],
  ["holdout", "Holdout groups", "Keep a comparison group that doesn't get the message"],
  ["pushMeasurement", "Push measurement", "Record who gets the automatic reminder pushes, for the Reminders results page"],
  ["sendTimeAB", "Send-time A/B test", "Allow splitting an SMS campaign between two send times"],
  ["autoDailySends", "Automatic daily sends", "Run the automations on their days and times"],
  ["channelLadder", "Channel ladder", "Abandoned checkout → push → SMS → AI call (replaces the abandoned-payment reminder)"]
];

const merge = (base, over) => {
  if (!over || typeof over !== "object" || Array.isArray(over)) return over === undefined ? base : over;
  const out = { ...base };
  for (const [k, v] of Object.entries(over)) out[k] = base && typeof base[k] === "object" && !Array.isArray(base[k]) ? merge(base[k], v) : v;
  return out;
};

const MarketingSettings = () => {
  const navigate = useNavigate();
  const { settings, permissions, loading, fetch, save } = useMarketingSettings();
  const [form, setForm] = useState(MARKETING_DEFAULTS);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetch(); }, []);
  useEffect(() => { if (settings) setForm(merge(MARKETING_DEFAULTS, settings)); }, [settings]);

  const readOnly = !permissions.canEdit;
  const setAt = (path, value) =>
    setForm((f) => {
      const next = structuredClone(f);
      const keys = path.split(".");
      let o = next;
      keys.slice(0, -1).forEach((k) => { o[k] = o[k] ?? {}; o = o[k]; });
      o[keys[keys.length - 1]] = value;
      return next;
    });
  const field = (path) => path.split(".").reduce((o, k) => (o === undefined ? undefined : o[k]), form);
  const num = (path, label, props = {}) => (
    <TextField label={label} type="number" margin="normal" size="small" sx={{ flex: "1 1 200px" }} disabled={readOnly}
      value={field(path) ?? ""} onChange={(e) => setAt(path, e.target.value === "" ? "" : Number(e.target.value))} {...props} />
  );
  const time = (path, label) => (
    <TextField label={label} type="time" margin="normal" size="small" sx={{ flex: "1 1 160px" }} disabled={readOnly}
      InputLabelProps={{ shrink: true }} value={field(path) || ""} onChange={(e) => setAt(path, e.target.value)} />
  );
  const toggle = (path, label, help) => (
    <Box sx={{ mb: 1 }}>
      <FormControlLabel control={<Switch checked={!!field(path)} disabled={readOnly} onChange={(e) => setAt(path, e.target.checked)} />} label={label} />
      {help && <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6, mt: -1 }}>{help}</Typography>}
    </Box>
  );

  const handleSave = async () => {
    setSaving(true);
    const { updatedAt, updatedByName, ...data } = form;
    if (!permissions.canEditAccess) delete data.access;
    await save(data);
    setSaving(false);
  };

  if (loading && !settings) return <Loading />;
  if (!settings) {
    return (
      <Box className="campaign-container">
        <ToastContainer position="top-right" autoClose={3000} />
        <Alert severity="error">Could not load marketing settings.</Alert>
      </Box>
    );
  }

  const bdt = <InputAdornment position="start">৳</InputAdornment>;
  const windows = form.timing?.sendWindows || [];

  return (
    <Box className="campaign-container">
      <Box className="campaign-header">
        <Button startIcon={<ArrowBack />} onClick={() => navigate("/campaigns")}>Campaigns</Button>
        <Typography variant="h5">Marketing Settings</Typography>
      </Box>

      {readOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>You can view these settings. Ask a Super Admin if you need to change them.</Alert>
      )}

      <Paper className="form-section">
        <Typography variant="h6" gutterBottom>Costs</Typography>
        <Typography variant="body2" color="text.secondary">Used to work out what each campaign and call cost. Each sent campaign keeps the SMS price it was sent with.</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          {num("costs.smsRatePerPart", "SMS price per part", { InputProps: { startAdornment: bdt }, inputProps: { min: 0, max: 10, step: 0.01 }, helperText: "BulkSMSBD price for one SMS part" })}
          {num("costs.callRatePerMinute", "Call price per minute", { InputProps: { startAdornment: bdt }, inputProps: { min: 0, max: 50, step: 0.01 }, helperText: "SIP trunk price (AI calling)" })}
          {num("costs.usdToBdt", "BDT per USD", { InputProps: { startAdornment: bdt }, inputProps: { min: 50, max: 500, step: 0.5 }, helperText: "For AI costs billed in dollars" })}
        </Box>
      </Paper>

      <Paper className="form-section">
        <Typography variant="h6" gutterBottom>Measuring Results</Typography>
        <Typography variant="body2" color="text.secondary">Defaults for new campaigns; each campaign can change them.</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          {num("attributionDays", "Count payments within (days)", { inputProps: { min: 1, max: 30 }, helperText: "A payment counts if it happens within this many days after the message (1–30)." })}
          {num("holdoutPercent", "Campaign holdout (%)", { inputProps: { min: 0, max: 50 }, helperText: "Share of a campaign's audience that doesn't get it, to compare against (0–50)." })}
          {num("reminderHoldoutPercent", "Reminder holdout (%)", { inputProps: { min: 0, max: 50 }, helperText: "Share of members who skip each automatic reminder push, to measure it (0 = everyone gets them)." })}
        </Box>
      </Paper>

      <Paper className="form-section">
        <Typography variant="h6" gutterBottom>Timing (Bangladesh time)</Typography>
        <Typography variant="subtitle2" sx={{ mt: 1 }}>Promotional SMS limits</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          {num("timing.promoSmsCap.count", "At most … SMS per member", { inputProps: { min: 0, max: 50 }, helperText: "0 = no limit. Hand-picked test lists aren't limited." })}
          {num("timing.promoSmsCap.days", "… in this many days", { inputProps: { min: 1, max: 60 } })}
          {num("budgets.smsDailyBdt", "Daily SMS budget", { InputProps: { startAdornment: bdt }, inputProps: { min: 0 }, helperText: "0 = no cap. Over budget waits for tomorrow." })}
        </Box>
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Quiet hours (no promotional SMS; they wait until the end)</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          {time("timing.quietHours.start", "From")}
          {time("timing.quietHours.end", "Until")}
        </Box>
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Best send windows (suggested in the campaign form, used by the ladder and best-hour sends)</Typography>
        {windows.map((w, i) => (
          <Box key={i} sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <TextField type="time" size="small" margin="dense" label="Start" InputLabelProps={{ shrink: true }} disabled={readOnly}
              value={w.start} onChange={(e) => setAt("timing.sendWindows", windows.map((x, j) => (j === i ? { ...x, start: e.target.value } : x)))} />
            <TextField type="time" size="small" margin="dense" label="End" InputLabelProps={{ shrink: true }} disabled={readOnly}
              value={w.end} onChange={(e) => setAt("timing.sendWindows", windows.map((x, j) => (j === i ? { ...x, end: e.target.value } : x)))} />
            <IconButton disabled={readOnly || windows.length <= 1} onClick={() => setAt("timing.sendWindows", windows.filter((_, j) => j !== i))}><Delete /></IconButton>
          </Box>
        ))}
        <Button size="small" startIcon={<Add />} disabled={readOnly || windows.length >= 4} onClick={() => setAt("timing.sendWindows", [...windows, { start: "12:00", end: "13:00" }])}>Add window</Button>
        <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
          <Typography variant="body2" sx={{ mr: 1 }}>Preferred days:</Typography>
          {DAY_NAMES.map((d, i) => {
            const on = (form.timing?.preferredDays || []).includes(i);
            return (
              <Chip key={d} label={d} color={on ? "primary" : "default"} variant={on ? "filled" : "outlined"} disabled={readOnly}
                onClick={() => setAt("timing.preferredDays", on ? form.timing.preferredDays.filter((x) => x !== i) : [...(form.timing.preferredDays || []), i])} />
            );
          })}
        </Box>
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Automatic reminder pushes</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          {time("timing.reminders.incompleteProfile", "Incomplete profile / face verification")}
          {time("timing.reminders.unreadNotifications", "Unread notifications")}
          <FormControl size="small" margin="normal" sx={{ flex: "1 1 140px" }} disabled={readOnly}>
            <InputLabel>New profiles day</InputLabel>
            <Select label="New profiles day" value={form.timing?.reminders?.newProfilesDay ?? 0} onChange={(e) => setAt("timing.reminders.newProfilesDay", Number(e.target.value))}>
              {DAY_NAMES.map((d, i) => <MenuItem key={d} value={i}>{d}</MenuItem>)}
            </Select>
          </FormControl>
          {time("timing.reminders.newProfilesTime", "New profiles time")}
        </Box>
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Abandoned-payment reminder</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          {num("timing.abandonedReminder.delayMinutes", "First reminder after (minutes)", { inputProps: { min: 5, max: 1440 } })}
          {num("timing.abandonedReminder.minGapHours", "Then at least (hours) apart", { inputProps: { min: 1, max: 48 } })}
        </Box>
      </Paper>

      <Paper className="form-section">
        <Typography variant="h6" gutterBottom>Channel Ladder</Typography>
        <Typography variant="body2" color="text.secondary">
          For each abandoned checkout, cheapest first, each step only if they still haven't paid. Turn it on under Features.
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          {num("timing.ladder.pushAfterMinutes", "Push after (minutes)", { inputProps: { min: 5, max: 1440 } })}
          {num("timing.ladder.smsAfterMinutes", "SMS after (minutes, then the next send window)", { inputProps: { min: 15, max: 2880 } })}
          {num("timing.ladder.minAmount", "AI call only for checkouts of at least", { InputProps: { startAdornment: bdt }, inputProps: { min: 0 } })}
        </Box>
        {toggle("timing.ladder.callNextDay", "AI call the next day in a call window", "Only when AI calling is switched on")}
        <TextField label="Ladder SMS text" fullWidth margin="normal" size="small" disabled={readOnly} inputProps={{ maxLength: 160 }}
          value={form.timing?.ladder?.smsText || ""} onChange={(e) => setAt("timing.ladder.smsText", e.target.value)}
          helperText={`{TrackingLink} opens the profile they tried to unlock. ${(form.timing?.ladder?.smsText || "").length}/160`} />
      </Paper>

      <Paper className="form-section">
        <Typography variant="h6" gutterBottom>Opt-out Rules</Typography>
        {toggle("optOut.respectPromotionalSms", "Respect the member's \"Offers by SMS and call\" switch")}
        {toggle("optOut.respectPushSwitchForSms", "Members who turned notifications off get no promotional SMS either")}
      </Paper>

      <Paper className="form-section">
        <Typography variant="h6" gutterBottom>Tracking Links</Typography>
        <Typography variant="subtitle2">Link text in SMS</Typography>
        <RadioGroup value={form.shortLinkStyle} onChange={(e) => setAt("shortLinkStyle", e.target.value)}>
          <FormControlLabel value="full" disabled={readOnly} control={<Radio />} label="https://bibahobangla.com/s/AB12CD (always clickable)" />
          <FormControlLabel value="short" disabled={readOnly} control={<Radio />} label="bibahobangla.com/s/AB12CD (8 characters shorter, may save an SMS part; most phones still make it a link)" />
        </RadioGroup>
        <Typography variant="subtitle2" sx={{ mt: 1 }}>Android members without the app</Typography>
        <RadioGroup value={form.noApp?.android || "playstore"} onChange={(e) => setAt("noApp.android", e.target.value)}>
          <FormControlLabel value="playstore" disabled={readOnly} control={<Radio />} label="Play Store (installs from the link are credited to the campaign)" />
          <FormControlLabel value="web" disabled={readOnly} control={<Radio />} label="Web app" />
        </RadioGroup>
      </Paper>

      <Paper className="form-section">
        <Typography variant="h6" gutterBottom>Features</Typography>
        {FEATURES.map(([key, label, help]) => <Box key={key}>{toggle(`features.${key}`, label, help)}</Box>)}
      </Paper>

      <Paper className="form-section">
        <Typography variant="h6" gutterBottom>Access</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Area</TableCell>
                {ROLES.map(([r, l]) => <TableCell key={r} align="center">{l}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {AREAS.map(([area, label]) => (
                <TableRow key={area}>
                  <TableCell>{label}</TableCell>
                  {ROLES.map(([role]) => {
                    const list = form.access?.[area] || [];
                    return (
                      <TableCell key={role} align="center">
                        <Checkbox
                          size="small"
                          checked={role === "Supper" || list.includes(role)}
                          disabled={role === "Supper" || !permissions.canEditAccess}
                          onChange={(e) => setAt(`access.${area}`, e.target.checked ? [...new Set([...list, role])] : list.filter((r) => r !== role))}
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Only a Super Admin can change this table, and Super Admins always have access.
        </Typography>
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mt: 3, flexWrap: "wrap" }}>
        <Typography variant="caption" color="text.secondary">
          {settings.updatedAt && `Last changed ${new Date(settings.updatedAt).toLocaleString("en-US", { timeZone: "Asia/Dhaka" })}${settings.updatedByName ? ` by ${settings.updatedByName}` : ""}`}
        </Typography>
        <Button variant="contained" onClick={handleSave} disabled={readOnly || saving}>{saving ? "Saving..." : "Save"}</Button>
      </Box>
    </Box>
  );
};

export default MarketingSettings;
